import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PopoverStaysOpen, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  getDealStatusDisplay,
  getStatusBadgeClasses,
  DEAL_STATUS_PICKER_OPTIONS,
} from '@/lib/api/myTenders';
import StatusSymbol from '@/components/procurement/StatusSymbol';

export default function DealStatusPicker({
  dealId,
  augment,
  variant = 'badge',
  onStatusChange,
}) {
  const [open, setOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const status = augment?.status ?? null;
  const display = getDealStatusDisplay(status);
  const badgeClasses = getStatusBadgeClasses(status);

  const commitStatus = async (nextStatus, nextNote) => {
    if (!onStatusChange) return;
    setSaving(true);
    try {
      await onStatusChange(dealId, nextStatus, nextNote);
      setOpen(false);
      setPendingStatus(null);
      setNote('');
    } catch {
      // Parent reverts state and shows toast.
    } finally {
      setSaving(false);
    }
  };

  const handlePick = (value) => {
    if (value === 'untouched') {
      commitStatus('untouched');
      return;
    }
    if (value === 'passed' || value === 'lost') {
      setPendingStatus(value);
      setNote('');
      return;
    }
    commitStatus(value);
  };

  const pickerBody = pendingStatus ? (
    <div className="space-y-2">
      <p className="text-xs text-gray-600">Why? (optional)</p>
      <Input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Brief note"
        className="h-8 text-xs"
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          className="h-7 text-xs flex-1"
          disabled={saving}
          onClick={() => commitStatus(pendingStatus, note.trim() || undefined)}
        >
          Save
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
          onClick={() => setPendingStatus(null)}
        >
          Back
        </Button>
      </div>
    </div>
  ) : (
    <div className="space-y-1">
      {DEAL_STATUS_PICKER_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-gray-100 flex items-center gap-2"
            onClick={() => handlePick(opt.value)}
          >
            <StatusSymbol status={opt.value === 'untouched' ? null : opt.value} size={14} />
            {opt.label}
          </button>
      ))}
    </div>
  );

  if (variant === 'dot') {
    return (
      <TooltipProvider delayDuration={200}>
        <PopoverStaysOpen open={open} onOpenChange={setOpen}>
          <Tooltip open={open ? false : undefined}>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center min-w-[1.25rem] min-h-[1.25rem] w-5 h-5 text-base leading-none cursor-pointer rounded shrink-0 overflow-visible bg-transparent hover:ring-1 hover:ring-gray-300/60"
                  aria-label={`Status: ${display.label}. Click to change.`}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <StatusSymbol status={status} size={16} />
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs">
              {display.label}
            </TooltipContent>
          </Tooltip>
          <PopoverContent
            className="w-48 p-2"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onClick={(e) => e.stopPropagation()}
          >
            {pickerBody}
          </PopoverContent>
        </PopoverStaysOpen>
      </TooltipProvider>
    );
  }

  return (
    <PopoverStaysOpen open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded border transition-colors ${badgeClasses}`}
          onClick={(e) => e.stopPropagation()}
        >
          <StatusSymbol status={status} size={14} />
          <span>{display.label}</span>
          <ChevronDown className="w-3 h-3 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-48 p-2"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}
      >
        {pickerBody}
      </PopoverContent>
    </PopoverStaysOpen>
  );
}
